/**
 * Year-to-pixel mapping for one band.
 *
 * The engine never holds a pixel step of its own: a Scale is always handed in
 * (implementation-plan.md §5.1). On screen it comes from a comfortable step per
 * tick; in print it comes from the usable page width, so a band always occupies
 * exactly one page across.
 */

import type { Band } from '../model/types';

export interface Scale {
  readonly pxPerYear: number;
  /** Width of the band body, in pixels. */
  readonly width: number;
  /** Pixel offset from the band's left edge. */
  x(year: number): number;
}

function createScale(band: Band, pxPerYear: number): Scale {
  return {
    pxPerYear,
    width: (band.end - band.start) * pxPerYear,
    x: (year) => (year - band.start) * pxPerYear,
  };
}

/** Screen scale: every tick gets the same comfortable width. */
export function scaleForTickWidth(band: Band, pxPerTick: number): Scale {
  return createScale(band, pxPerTick / band.step);
}

/** Print scale: the band is stretched to the width available on the page. */
export function scaleForBandWidth(band: Band, width: number): Scale {
  return createScale(band, width / (band.end - band.start));
}
